import { createContext, useContext, useState, useMemo } from "react";

const STORAGE_KEY_ROLE = "dentaparse_test_role";
const STORAGE_KEY_CLINICS = "dentaparse_test_clinics";

const RoleTestContext = createContext({
  testRoleId: null,
  testClinics: [],
  setTestRoleId: () => {},
});

export const ROLE_TEST_META = {
  1: {
    role: 1,
    role_name: "organizer",
    label: "Organizer",
    color: "indigo",
    desc: "Full access to all clinics and all features.",
  },
  2: {
    role: 2,
    role_name: "admin",
    label: "Admin",
    color: "blue",
    desc: "Same as Organizer. Full admin access.",
  },
  3: {
    role: 3,
    role_name: "area_manager",
    label: "Area Manager",
    color: "violet",
    desc: "CRUD on assigned clinics. No payroll/finance.",
  },
  4: {
    role: 4,
    role_name: "practitioner_lead",
    label: "Practitioner Lead",
    color: "purple",
    desc: "CRUD on their single assigned clinic.",
  },
  5: {
    role: 5,
    role_name: "visitor",
    label: "Visitor",
    color: "gray",
    desc: "No access to any features.",
  },
  6: {
    role: 6,
    role_name: "observer",
    label: "Observer",
    color: "teal",
    desc: "Read-only across all clinics. Sees finance tabs.",
  },
  7: {
    role: 7,
    role_name: "payroll",
    label: "Payroll",
    color: "amber",
    desc: "Finance-only: JE, Check, Deposit Sheet, ADP Netsuite.",
  },
  8: {
    role: 8,
    role_name: "bookkeeper",
    label: "Bookkeeper",
    color: "orange",
    desc: "Read-only on assigned clinics.",
  },
  9: {
    role: 9,
    role_name: "finance_coordinator",
    label: "Finance Coordinator",
    color: "emerald",
    desc: "All clinics, but only the Cash Flow section.",
  },
};

export function RoleTestProvider({ children }) {
  const [testRoleId, setTestRoleIdState] = useState(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY_ROLE);
    return stored ? Number(stored) : null;
  });

  const [testClinics, setTestClinicsState] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY_CLINICS) || "null") || [];
    } catch {
      return [];
    }
  });

  const setTestRoleId = (roleId, clinics = []) => {
    if (roleId == null) {
      sessionStorage.removeItem(STORAGE_KEY_ROLE);
      sessionStorage.removeItem(STORAGE_KEY_CLINICS);
      setTestRoleIdState(null);
      setTestClinicsState([]);
    } else {
      sessionStorage.setItem(STORAGE_KEY_ROLE, String(roleId));
      sessionStorage.setItem(STORAGE_KEY_CLINICS, JSON.stringify(clinics));
      setTestRoleIdState(Number(roleId));
      setTestClinicsState(clinics);
    }
  };

  const value = useMemo(
    () => ({ testRoleId, testClinics, setTestRoleId }),
    [testRoleId, testClinics],
  );

  return <RoleTestContext.Provider value={value}>{children}</RoleTestContext.Provider>;
}

export function useRoleTest() {
  return useContext(RoleTestContext);
}
